import { type GoalsExperienceResponse } from '../../../models/goal';
import { getDateOffset } from '../support/date-utils';

export const devPreviewQuote = {
  text: 'Leadership is not about being in charge. Leadership is about taking care of those in your charge.',
  author: 'Simon Sinek'
};

export const baseGoalsExperience: GoalsExperienceResponse = {
  activePackage: null,
  draft: null
};

// WHY: Jordan's marketing screenshots should show the Goals surface as part of an active
// executive journey, rather than presenting an empty state that contradicts the profile and
// multi-program session story.
export const jordanGoalsExperience: GoalsExperienceResponse = {
  activePackage: {
    id: 'dev-goal-jordan-1',
    status: 'active',
    title: 'Create clarity and alignment through AI-enabled change',
    description:
      'Build the leadership rhythms and communication practices that help senior leaders navigate change with confidence.',
    rationale:
      'Jordan’s focus is not only to move an AI-enabled change agenda forward, but to create the clarity and shared ownership that makes the change sustainable.',
    leadingMeasures: [
      {
        id: 'dev-goal-jordan-leading-1',
        type: 'leading',
        label: 'Alignment conversations with senior stakeholders',
        description: 'Hold focused conversations that surface trade-offs, decisions, and ownership.',
        target: { value: 3, unit: 'per week' },
        latestCheckIn: {
          status: 'on_track',
          note: 'Three focused conversations completed this week; the next decision owner is clear.',
          updatedAt: getDateOffset(-2, 9)
        }
      },
      {
        id: 'dev-goal-jordan-leading-2',
        type: 'leading',
        label: 'Weekly change narrative shared',
        description: 'Share a concise update that connects the change work to customer and team impact.',
        target: { value: 1, unit: 'per week' },
        latestCheckIn: {
          status: 'some_movement',
          note: 'The narrative is drafted; the next step is to make the team-level implications more concrete.',
          updatedAt: getDateOffset(-2, 9)
        }
      }
    ],
    laggingMeasures: [
      {
        id: 'dev-goal-jordan-lagging-1',
        type: 'lagging',
        label: 'Senior leader clarity score',
        description: 'Pulse how clearly senior leaders understand the direction and their role in it.',
        target: { value: 4, unit: 'out of 5' },
        latestCheckIn: {
          status: 'clear_progress',
          note: 'Early feedback shows stronger alignment on the direction; role clarity still needs attention.',
          updatedAt: getDateOffset(-5, 9)
        }
      }
    ],
    plan: {
      cadence: 'weekly',
      note: 'Review the conversation rhythm every Friday and use the monthly leadership pulse to test whether clarity is compounding.'
    },
    updatedAt: getDateOffset(-2, 9)
  },
  draft: null
};

export const ceoGoalsExperience: GoalsExperienceResponse = {
  activePackage: {
    id: 'dev-goal-ceo-1',
    status: 'active',
    title: 'Lead Northstar Systems through responsible AI growth',
    description:
      'Create the strategic clarity, leadership capacity, and operating rhythm needed to scale the company’s next chapter.',
    rationale:
      'As CEO, Evan needs a place to work both the external transformation agenda and the internal leadership system that makes growth sustainable.',
    leadingMeasures: [
      {
        id: 'dev-goal-ceo-leading-1',
        type: 'leading',
        label: 'Strategic leadership conversations',
        description: 'Create space each week for the leadership team to align on the few decisions that matter most.',
        target: { value: 2, unit: 'per week' },
        latestCheckIn: {
          status: 'on_track',
          note: 'The leadership team has a clear decision agenda for the next operating cycle.',
          updatedAt: getDateOffset(-3, 9)
        }
      },
      {
        id: 'dev-goal-ceo-leading-2',
        type: 'leading',
        label: 'AI operating-model experiments',
        description: 'Run small, visible experiments that turn the AI strategy into practical operating change.',
        target: { value: 1, unit: 'per month' },
        latestCheckIn: {
          status: 'some_movement',
          note: 'The first experiment is underway; the success signal needs to be made more measurable.',
          updatedAt: getDateOffset(-3, 9)
        }
      }
    ],
    laggingMeasures: [
      {
        id: 'dev-goal-ceo-lagging-1',
        type: 'lagging',
        label: 'Leadership-team alignment score',
        description: 'Pulse whether the leadership team understands the direction, trade-offs, and next decisions.',
        target: { value: 4, unit: 'out of 5' },
        latestCheckIn: {
          status: 'clear_progress',
          note: 'The team is more aligned on the direction; decision speed is the next opportunity.',
          updatedAt: getDateOffset(-7, 9)
        }
      }
    ],
    plan: {
      cadence: 'weekly',
      note: 'Use AI Executive Coaching for the transformation agenda and Executive Coaching for the leadership rhythm; review both threads every Friday.'
    },
    updatedAt: getDateOffset(-3, 9)
  },
  draft: null
};
